import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  Divider,
  Grid,
  Link,
  TextField,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import { GetTransactionResult } from "upcx-js/dist/upcxjs-rpc-interfaces";
import { Transaction } from "../../openApi/api";
import { useMemo } from "react";
import { CalculatePassTime, convertTimestampToLocal } from "../../utils";
import { useRecoilState } from "recoil";
import { ThemeLight } from "../../state/ThemeLight";

interface Props {
  transaction?: GetTransactionResult;
  txSyncData?: Transaction;
}

const Overview = ({ transaction, txSyncData }: Props) => {
  const [themeLight] = useRecoilState(ThemeLight);

  const currentTime = useMemo(
    () => new Date(txSyncData?.timestamp.substring(0, 19) ?? ""),
    [txSyncData]
  );
  const passTime = useMemo(() => {
    return CalculatePassTime(currentTime);
  }, [currentTime]);

  const txData = useMemo(() => {
    return JSON.parse(txSyncData?.data ?? "{}");
  }, [txSyncData]);

  return (
    <Container className="container-xxl my-2">
      <Box className="card p-4">
        <Grid container className="mb-4">
          <Grid item xs={3} className="text-dt d-flex align-items-center">
            <HelpOutlineIcon fontSize="small" className="me-1" />
            Transaction Hash:
          </Grid>
          <Grid item xs={9} className="">
            <Box className="d-flex align-items-center gap-2">
              {transaction?.id}
              <ContentCopyIcon
                fontSize="small"
                className="text-icon cursor-pointer"
                onClick={() =>
                  navigator.clipboard.writeText(transaction?.id ?? "")
                }
              />
            </Box>
          </Grid>
        </Grid>
        <Grid container className="mb-4">
          <Grid item xs={3} className="text-dt d-flex align-items-center">
            <HelpOutlineIcon fontSize="small" className="me-1" />
            Attributes:
          </Grid>
          <Grid item xs={9}>
            <Box className="d-flex flex-wrap gap-2">
              <span id="ContentPlaceHolder1_spanTxnType">
                <span className="badge bg-secondary bg-opacity-10 border border-secondary border-opacity-25 text-dark fw-medium text-start text-wrap py-1.5 px-2">
                  <span className="text-muted">Txn Type:</span>{" "}
                  {txSyncData?.actionName}
                </span>
              </span>
              <span className="badge bg-secondary bg-opacity-10 border border-secondary border-opacity-25 text-dark fw-medium text-start text-wrap py-1.5 px-2">
                <span className="text-muted">Position In Block:</span> 0
              </span>
            </Box>
          </Grid>
        </Grid>
        <Grid container className="mb-4">
          <Grid item xs={3} className="text-dt d-flex align-items-center">
            <HelpOutlineIcon fontSize="small" className="me-1" />
            Status:
          </Grid>
          <Grid item xs={9} className="text-dt">
            <span
              className="badge bg-success bg-opacity-10 border border-success border-opacity-25 text-green-600 fw-medium text-start text-wrap py-1.5 px-2"
              data-bs-toggle="tooltip"
              data-bs-trigger="hover"
              data-bs-placement="top"
            >
              <CheckCircleIcon className="me-1 text-icon" />
              Success
            </span>
          </Grid>
        </Grid>

        <Grid container className="mb-4">
          <Grid item xs={3} className="text-dt d-flex align-items-center">
            <HelpOutlineIcon fontSize="small" className="me-1" />
            Block:
          </Grid>
          <Grid item xs={9}>
            <Box className="d-flex align-items-center gap-2">
              <CheckCircleIcon className="text-icon text-green-600" />
              <Link href={`/block/${transaction?.block_num}`}>
                {transaction?.block_num}
              </Link>
              <span
                className="badge bg-secondary bg-opacity-10 border border-secondary border-opacity-25 text-dark fw-medium text-start text-wrap py-1.5 px-2"
                data-bs-toggle="tooltip"
                data-bs-trigger="hover"
                data-bs-placement="top"
              >
                0 Block Confirmations
              </span>
            </Box>
          </Grid>
        </Grid>
        <Grid container className="mb-4">
          <Grid item xs={3} className="text-dt d-flex align-items-center">
            <HelpOutlineIcon fontSize="small" className="me-1" />
            Timestamp:
          </Grid>
          <Grid item xs={9}>
            <Box className="d-flex align-items-center gap-2">
              <AccessTimeIcon fontSize="small" />
              {passTime} ago (
              <span id="showUtcLocalDate" data-timestamp="1705414331">
                {convertTimestampToLocal(currentTime).toUTCString()}
              </span>
              )
            </Box>
          </Grid>
        </Grid>

        <Divider
          sx={
            themeLight
              ? { backgroundColor: "inherit" }
              : { backgroundColor: "#495057 !important" }
          }
        />

        <Grid container className="mt-4 mb-4">
          <Grid item xs={3} className="text-dt d-flex align-items-center">
            <HelpOutlineIcon fontSize="small" className="me-1" />
            From:
          </Grid>
          <Grid item xs={9}>
            <Box className="d-flex align-items-center gap-2">
              <Link href={`/address/${txSyncData?.sender}`}>
                {txSyncData?.sender}
              </Link>
              <ContentCopyIcon
                fontSize="small"
                className="text-icon cursor-pointer"
                onClick={() =>
                  navigator.clipboard.writeText(txSyncData?.sender ?? "")
                }
              />
            </Box>
          </Grid>
        </Grid>

        <Grid container className="mb-4">
          <Grid item xs={3} className="text-dt d-flex align-items-center">
            <HelpOutlineIcon fontSize="small" className="me-1" />
            To:
          </Grid>
          <Grid item xs={9}>
            <Box className="d-flex align-items-center gap-2">
              {txSyncData?.receiver.includes("upcx.") && (
                <AssignmentIcon className="text-icon" />
              )}
              <Link href={`/address/${txSyncData?.receiver}`}>
                {" "}
                {txSyncData?.receiver}{" "}
              </Link>
              <ContentCopyIcon
                fontSize="small"
                className="text-icon cursor-pointer"
                onClick={() =>
                  navigator.clipboard.writeText(txSyncData?.receiver ?? "")
                }
              />
            </Box>
          </Grid>
        </Grid>
        <Grid container className="mb-4">
          <Grid item xs={3} className="text-dt d-flex align-items-center">
            <HelpOutlineIcon fontSize="small" className="me-1" />
            Value:
          </Grid>
          <Grid item xs={9}>
            <Box className="d-flex align-items-center gap-2">
              {txData.quantity ?? txData.price ?? ""}
            </Box>
          </Grid>
        </Grid>
        {(txSyncData?.actionName === "swap" ||
          txSyncData?.actionName === "multitransfer") && (
          <Grid container className="mb-4">
            <Grid item xs={3} className="text-dt d-flex align-items-center">
              <HelpOutlineIcon fontSize="small" className="me-1" />
              Swap Info:
            </Grid>
            <Grid item xs={9}>
              <Box className="d-flex align-items-center gap-2">
                {txSyncData?.swapAmount
                  ? `${txSyncData.swapAmount} ${txSyncData.swapToken}`
                  : ""}
              </Box>
            </Grid>
          </Grid>
        )}
        <Divider
          sx={
            themeLight
              ? { backgroundColor: "inherit" }
              : { backgroundColor: "#495057 !important" }
          }
        />

        <Grid container className="mt-4 mb-4">
          <Grid item xs={3} className="text-dt d-flex align-items-center">
            <HelpOutlineIcon fontSize="small" className="me-1" />
            Tx Fee:
          </Grid>
          <Grid item xs={9}>
            <Box className="d-flex align-items-center gap-2">
              {txSyncData?.fee.toFixed(5)} UPC
              {/* 31.772691409 Gwei
              <span className="text-muted">(0.000000031772691409 UPC)</span> */}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box className="card p-4 mt-4">
        <Accordion
          sx={{
            border: "unset",
            boxShadow: "unset",
            padding: "0",
            "&::before": {
              backgroundColor: "transparent",
            },
            "&::after": {
              backgroundColor: "transparent",
            },
            backgroundColor: "inherit",

            ".MuiCollapse-wrapper": {
              backgroundColor: themeLight ? "inherit" : "#111111",
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ArrowDropDownIcon className="text-icon" />}
            aria-controls="panel1-content"
            id="panel1-header"
            sx={{
              border: "unset",
              boxShadow: "unset",
              padding: "0",
              margin: "0",
              backgroundColor: themeLight ? "inherit" : "#111111",
            }}
          >
            <Grid container className="">
              <Grid item xs={3} className="text-dt d-flex align-items-center">
                More Detail:
              </Grid>
              <Grid item xs={9}>
                <Link className="d-flex align-items-center">
                  <AddIcon className="text-icon" />
                  &nbsp; Click to show more
                </Link>
              </Grid>
            </Grid>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              padding: 0,
              margin: 0,
            }}
          >
            <Divider
              sx={
                themeLight
                  ? { backgroundColor: "inherit" }
                  : { backgroundColor: "#495057 !important" }
              }
            />

            <Grid container className="mb-4 mt-4">
              <Grid item xs={3} className="text-dt d-flex align-items-center">
                <HelpOutlineIcon fontSize="small" className="me-1" />
                Gas Limit & Usage by Txn:
              </Grid>
              <Grid item xs={9} className="text">
                0.00
                <span className="mx-2 text-secondary">|</span>
                0.00 (0%)
              </Grid>
            </Grid>

            <Grid container className="mb-4">
              <Grid item xs={3} className="text-dt d-flex align-items-center">
                <HelpOutlineIcon fontSize="small" className="me-1" />
                Gas Fees:
              </Grid>
              <Grid item xs={9}>
                <Tooltip title="31.772691409UPC" describeChild placement="top">
                  <span className="mb-1">
                    <span className="text-muted">Base:</span> 0.00 UPC
                  </span>
                </Tooltip>
                <span className="mx-2 text-secondary">|</span>

                <Tooltip
                  title="31.772691409 Gwei"
                  describeChild
                  placement="top"
                >
                  <span className="mb-1">
                    <span className="text-muted">Max:</span> 0.00 UPC
                  </span>
                </Tooltip>

                <span className="mx-2 text-secondary">|</span>

                <Tooltip title="0UPC" describeChild placement="top">
                  <span className="mb-1">
                    <span className="text-muted">Max Priority:</span> 0.00 UPC
                  </span>
                </Tooltip>
              </Grid>
            </Grid>

            <Grid container className="mb-4">
              <Grid item xs={3} className="text-dt d-flex align-items-center">
                <HelpOutlineIcon fontSize="small" className="me-1" />
                Burnt & Txn Savings Fees:
              </Grid>
              <Grid item xs={9}>
                <span className="badge bg-secondary bg-opacity-10 border border-secondary border-opacity-25 text-dark fw-medium text-start text-wrap py-1.5 px-2 me-2">
                  🔥 <span className="text-muted">Burnt:</span> 0<b>.</b>
                  00 UPC ($0.00)
                </span>
                <span className="badge bg-secondary bg-opacity-10 border border-secondary border-opacity-25 text-dark fw-medium text-start text-wrap py-1.5 px-2">
                  💸 <span className="text-muted">Txn Savings:</span> 0.00 UPC
                  ($0.00)
                </span>
              </Grid>
            </Grid>

            <Divider />

            <Grid container className="mb-4 mt-4">
              <Grid item xs={3} className="text-dt d-flex align-items-center">
                <HelpOutlineIcon fontSize="small" className="me-1" />
                Input Data:
              </Grid>
              <Grid item xs={9}>
                <Tooltip
                  title="The Binary data ..."
                  describeChild
                  placement="top"
                >
                  <TextField
                    placeholder="0x"
                    minRows={2}
                    value={txSyncData?.data}
                    multiline
                    fullWidth
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{
                      backgroundColor: themeLight
                        ? "inherit"
                        : "#111111 !important",
                      ".MuiTextarea-textarea": themeLight
                        ? {}
                        : { color: "white" },
                    }}
                  />
                </Tooltip>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>

      <Box className="card p-4 mt-4">
        <Grid container className="mb-4 mt-4">
          <Grid item xs={3} className="text-dt d-flex align-items-center">
            <HelpOutlineIcon fontSize="small" className="me-1" />
            Private Note:
          </Grid>
          <Grid item xs={9}>
            <Box className="d-flex flex-wrap gap-2">
              To access the <strong>Private Note</strong> feature, you must be
              <Link href="/login">Logged In</Link>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box className="mt-4">
        <p className="d-flex align-items-baseline fs-sm text-muted gap-1 mb-3">
          <TipsAndUpdatesIcon className="small-icon" />
          <span>
            A transaction is a cryptographically signed instruction that changes
            the blockchain state. Block explorers track the details of all
            transactions in the network. Learn more about transactions in our{" "}
            <Link target="_blank" href="/knowledge">
              Knowledge Base
            </Link>
            .
          </span>
        </p>
      </Box>
    </Container>
  );
};

export default Overview;
